import { VCPM } from '../models/project.model';
import { VcfDomain } from '../models/vcf-domain.model';
import { DataProvider } from '../models/data-provider.model';
import { AppConstants } from '../models/app-constants.model';

/**
 * Ported as-is from AdapterService.projectDetailsPageAdapter (adapter.service.ts:19-33).
 *
 * Gap E mutation semantics: mutates `productSelection` in place (assigns into its nested
 * products/deploymentModel/architecture fields) AND returns a new object referencing it.
 * The caller's productSelection object is mutated as a side effect - preserved as-is.
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function projectDetailsPageAdapter(projectMetadata: VCPM.Metadata, productSelection: any) {
  const metadata: VCPM.ProjectMetadata = {
    companyName: projectMetadata?.companyName,
    customerId: projectMetadata?.customerId,
    description: projectMetadata?.description,
    isTestProject: projectMetadata?.isTestProject,
    projectName: projectMetadata?.projectName,
  };
  productSelection.products.default.name = projectMetadata ? projectMetadata.product : productSelection.products.default.name;
  productSelection.products.default.version.default = projectMetadata ? projectMetadata.version : productSelection.products.default.version.default;
  productSelection.deploymentModel.default.value = projectMetadata ? projectMetadata?.deploymentModel : productSelection.deploymentModel.default.value;
  productSelection.architecture.default.value = projectMetadata ? projectMetadata?.architecture : productSelection.architecture.default.value;

  return { metadata, productSelection };
}

/**
 * Ported as-is from AdapterService.getWorkloadComponentsFromProject (adapter.service.ts:35-37).
 */
export function getWorkloadComponentsFromProject(project: VCPM.Project) {
  return project.data.workloadDomains?.map((ele: VCPM.WorkloadDomain) => ({ id: ele.id, selectedComponents: ele.selectedComponents }));
}

/**
 * Ported as-is from AdapterService.getManagementComponentsFromProject (adapter.service.ts:38-40).
 */
export function getManagementComponentsFromProject(project: VCPM.Project) {
  return project.data.managementDomain?.selectedComponents;
}

/**
 * Ported as-is from AdapterService.getBrickSizerLabel (adapter.service.ts:153-190).
 */
export function getBrickSizerLabel(brickSizingId: string): string {
  let label = '';
  switch (brickSizingId.toUpperCase()) {
    case 'TINY':
      label = 'Tiny';
      break;
    case 'EXTRA_SMALL':
    case 'EXTRA-SMALL':
    case 'X-SMALL':
      label = 'X-Small';
      break;
    case 'SMALL':
      label = 'Small';
      break;
    case 'MEDIUM':
      label = 'Medium';
      break;
    case 'LARGE':
      label = 'Large';
      break;
    case 'X-LARGE':
    case 'EXTRA_LARGE':
    case 'EXTRA-LARGE':
      label = 'X-Large';
      break;
    case '2X-LARGE':
    case '2XLARGE':
      label = '2X-Large';
      break;
    case 'STANDARD':
      label = 'Standard';
      break;
    case 'FIXED':
      label = 'Fixed';
      break;
  }
  return label;
}

/**
 * Model gap (not fixed): DataProvider.ResourceUtilizationPlan only declares avgMemoryConsumption/
 * avgConsumption, but getInputConfigFromDataProviderAPIResponse and
 * getWorkloadTypeConfigFromDataProviderAPIResponse both also populate cpuUtilization/
 * cpuConsumption/vmPercentage on it. The original code never surfaced this because the whole
 * object was untyped (`any`); extending locally here to preserve the real runtime shape without
 * widening the shared model. See "Behavior anomalies observed" in the S1 task report.
 */
interface ResourceUtilizationPlanExtended extends Partial<DataProvider.ResourceUtilizationPlan> {
  cpuUtilization?: DataProvider.ValueUnit;
  cpuConsumption?: DataProvider.ValueUnit;
  vmPercentage?: DataProvider.ValueUnit;
}

enum SizeType {
  Tiny = 0,
  ExtraSmall = 1,
  Small = 2,
  Medium = 3,
  Large = 4,
  XLarge = 5,
  TwoXLarge = 6,
  Standard = 7,
}

// Module-private, not exported - ported as-is from adapter.service.ts:986-1002.
function getEnumValue(type: string): number {
  switch (type.toLowerCase()) {
    case 'tiny':
      return SizeType.Tiny;
    case 'extra_small':
    case 'extra-small':
    case 'x-small':
      return SizeType.ExtraSmall;
    case 'small':
      return SizeType.Small;
    case 'medium':
      return SizeType.Medium;
    case 'large':
      return SizeType.Large;
    case 'x-large':
    case 'extra_large':
    case 'extra-large':
      return SizeType.XLarge;
    case '2xlarge':
    case '2x-large':
      return SizeType.TwoXLarge;
    case 'standard':
      return SizeType.Standard;
    default:
      return Number.MAX_SAFE_INTEGER;
  }
}

/**
 * Ported as-is from AdapterService.getWldComponentsInputConfig (adapter.service.ts:41-152).
 *
 * Gap E mutation semantics: deep-clones `config` via JSON round-trip and mutates only the clone
 * (vConfig) - the original `config` argument is never mutated. Unlike S5's transforms, this one
 * has no aliasing bug.
 */
export function getWldComponentsInputConfig(
  components: DataProvider.Component[],
  config: VcfDomain.Group | undefined,
): VcfDomain.Group {
  const vConfig: VcfDomain.Group = JSON.parse(JSON.stringify(config)); // config used for updations
  if (config && config.subGroups && Array.isArray(config.subGroups))
    //loop through sub groups
    for (let subgroupIndex = 0; subgroupIndex < config.subGroups.length; subgroupIndex++) {
      const matchedComponent = components.find((ele) => ele.name == config.subGroups[subgroupIndex].subGroupId);
      if (matchedComponent) vConfig.subGroups[subgroupIndex].id = matchedComponent.id;
      else console.log('***Unmatched Component**** ' + config.subGroups[subgroupIndex].subGroupId);
      //check for inputs
      const inputs = config.subGroups[subgroupIndex].inputs;
      if (inputs && Array.isArray(inputs)) {
        for (let inputsIndex = 0; inputsIndex < inputs.length; inputsIndex++) {
          if (inputs[inputsIndex].type == 'COMPONENT_DROPDOWN' || inputs[inputsIndex].type == 'MULTI_COMPONENT_DROPDOWN') {
            let componentName: string | undefined;
            if (inputs[inputsIndex].componentId) {
              componentName = inputs[inputsIndex].componentId;
            } else {
              componentName = config.subGroups[subgroupIndex].subGroupId;
            }
            const componentData: DataProvider.Component | undefined = components.filter((ele) => ele.name == componentName)[0];

            const isFixedComponentSizing = vConfig.subGroups[subgroupIndex].inputs![inputsIndex].options!.some(
              (ele) => ele.id.toLowerCase() == 'fixed',
            );
            if (componentData == undefined && !isFixedComponentSizing) {
              console.log('***Unknown Component**** ' + componentName);
              continue;
            }
            if (componentData != undefined) {
              // update default value
              const defaultSizing = componentData.defaultSizing;
              const sizingExists = componentData.sizing.some((s) => s.id === defaultSizing);
              vConfig.subGroups[subgroupIndex].inputs![inputsIndex].default = sizingExists
                ? defaultSizing
                : componentData.sizing[0]?.id;

              // update minmum number of VMs
              vConfig.subGroups[subgroupIndex].inputs![inputsIndex].minNumberOfVms = componentData.minNumberOfVms;
            }
            const subgroupInputs = config.subGroups[subgroupIndex]?.inputs;
            if (subgroupInputs) {
              // check if fixed size
              if (isFixedComponentSizing) {
                //loop through overheads
                if (subgroupInputs) {
                  const fixedComponents = subgroupInputs[inputsIndex].componentSizing;
                  if (fixedComponents && Array.isArray(fixedComponents) && fixedComponents.length > 0) {
                    // Composite fixed-sizing inputs (e.g. HCX) bundle multiple backend components
                    // under one card with no matching umbrella componentName, so minNumberOfVms is
                    // never set via the componentData lookup above - sum it from the overheads instead.
                    let totalMinNumberOfVms = 0;
                    for (let fixedComponentsindex = 0; fixedComponentsindex < fixedComponents.length; fixedComponentsindex++) {
                      const overheadComponents = fixedComponents[fixedComponentsindex].overheads;
                      for (let overheadComponentsIndex = 0; overheadComponentsIndex < overheadComponents.length; overheadComponentsIndex++) {
                        const overheadComponent = overheadComponents[overheadComponentsIndex];
                        // map id to component name
                        const component = components.filter((ele) => ele.name == overheadComponent.id);
                        if (component.length == 0) {
                          console.error('***No matching components for overhead component:' + overheadComponent.id);
                        } else {
                          // update resources
                          const matchedBackendSize = component[0].sizing[0];
                          if (matchedBackendSize) {
                            vConfig.subGroups[subgroupIndex].inputs![inputsIndex].componentSizing![fixedComponentsindex].overheads[
                              overheadComponentsIndex
                            ].resources = matchedBackendSize;
                          }
                          totalMinNumberOfVms += component[0].minNumberOfVms || 0;
                        }
                      }
                    }
                    vConfig.subGroups[subgroupIndex].inputs![inputsIndex].minNumberOfVms = totalMinNumberOfVms;
                  }
                }
              } else {
                // build options object
                vConfig.subGroups[subgroupIndex].inputs![inputsIndex].options = componentData!.sizing
                  .map((ele) => ({ id: ele.id, label: getBrickSizerLabel(ele.id) }))
                  .sort((a, b) => getEnumValue(a.id) - getEnumValue(b.id));
                // build componentSizing object
                if (subgroupInputs[inputsIndex].componentSizing != undefined) {
                  const componentSizingConfig = subgroupInputs[inputsIndex].componentSizing;
                  if (componentSizingConfig?.length) {
                    for (let sizingIndex = 0; sizingIndex < componentSizingConfig?.length; sizingIndex++) {
                      const overheads = componentSizingConfig[sizingIndex].overheads;
                      for (let overheadIndex = 0; overheadIndex < overheads.length; overheadIndex++) {
                        const currentSizeId = componentSizingConfig[sizingIndex].id;
                        if (inputs[inputsIndex].type === 'MULTI_COMPONENT_DROPDOWN') {
                          const overheadComponentId = overheads[overheadIndex].id;
                          const matchedDpComponent = components.find((ele) => ele.name === overheadComponentId);
                          if (matchedDpComponent) {
                            const matchedSize = matchedDpComponent.sizing.find((sz) => sz.id === currentSizeId);
                            if (matchedSize) {
                              vConfig.subGroups[subgroupIndex].inputs![inputsIndex].componentSizing![sizingIndex].overheads[
                                overheadIndex
                              ].resources = matchedSize;
                              vConfig.subGroups[subgroupIndex].inputs![inputsIndex].componentSizing![sizingIndex].overheads[
                                overheadIndex
                              ].vmCount = matchedDpComponent.minNumberOfVms;
                            }
                          }
                        } else {
                          // Default behavior: use the selected size resources from the current component
                          const matchedBackendSize = componentData!.sizing.find((ele) => ele.id == currentSizeId);
                          if (matchedBackendSize) {
                            vConfig.subGroups[subgroupIndex].inputs![inputsIndex].componentSizing![sizingIndex].overheads[
                              overheadIndex
                            ].resources = matchedBackendSize;
                            // Add size-specific VM count if available (e.g., VCF_AUTOMATION has different VM counts per size)
                            // eslint-disable-next-line @typescript-eslint/no-explicit-any
                            if ((matchedBackendSize as any).numberOfVMs !== undefined) {
                              vConfig.subGroups[subgroupIndex].inputs![inputsIndex].componentSizing![sizingIndex].overheads[
                                overheadIndex
                                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                              ].vmCount = (matchedBackendSize as any).numberOfVMs;
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
      }
    }
  return vConfig;
}

/**
 * Ported as-is from AdapterService.getWorkloadsFromProject (adapter.service.ts:192-194).
 */
export function getWorkloadsFromProject(project: VCPM.Project | undefined, domainId: number | undefined) {
  return project?.data.workloadDomains ? project.data.workloadDomains?.filter((ele) => ele.id == domainId)[0]?.workloadList : [];
}

/**
 * Ported as-is from AdapterService.getManagementDomainConfigurationFromProject (adapter.service.ts:196-198).
 */
export function getManagementDomainConfigurationFromProject(project: VCPM.Project | undefined) {
  return project?.data.managementDomain ? project.data.managementDomain.configuration : undefined;
}

/**
 * Ported as-is from AdapterService.getInputConfigFromDataProviderAPIResponse (adapter.service.ts:200-597).
 *
 * DataProvider.HardwareConfiguration/ClusterConfiguration/WorkloadDetails/AdvancedSettings declare
 * several fields as non-optional even though this builder only ever populates the subset that has
 * a matching `config.id` in the switch below - built with Partial<...> locally and cast to the
 * shared interface at the return, matching the original's own `{} as any` escape hatch without
 * introducing a fresh `any`.
 */
export function getInputConfigFromDataProviderAPIResponse(
  inputConfig: DataProvider.InputConfigurationResponse,
): DataProvider.DefaultInputConfig {
  let storageConfiguration = {} as DataProvider.StorageConfiguration;
  const hardwareConfiguration: Partial<DataProvider.HardwareConfiguration> = {};
  const clusterConfiguration: Partial<DataProvider.ClusterConfiguration> = {};
  const workloadDefinition: DataProvider.Workload = {
    workload: {
      default: {
        value: inputConfig.queries.workloadoption,
        unit: null,
      },
      options: AppConstants.WORKLOADS,
    },
    workloadType: {
      default: {
        value: inputConfig.queries.deploymentcode,
        unit: null,
      },
      options: AppConstants.GPW_SUPPORTED_WORKLOAD_TYPES,
    },
  };
  const workloadDetails: Partial<DataProvider.WorkloadDetails> = {};
  const advancedSettings: {
    vSANOverheads: Partial<DataProvider.VSANOverheads>;
    resourceUtilizationPlan: ResourceUtilizationPlanExtended;
  } = {
    vSANOverheads: {},
    resourceUtilizationPlan: {},
  };

  if (inputConfig && inputConfig.configurations.length > 0) {
    inputConfig.configurations.forEach((config: DataProvider.Configuration) => {
      switch (config.id) {
        //hardware config
        case 'totalSockets':
          hardwareConfiguration.sockets = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          break;
        case 'coresPerSocket':
          hardwareConfiguration.cores = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'clockSpeed':
          hardwareConfiguration.clockSpeed = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'cacheTierMediaRating':
          hardwareConfiguration.cacheTierMediaRating = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          break;
        case 'numberOfDriveSlots':
          hardwareConfiguration.numberOfDrives = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'capacityDriveSize':
          hardwareConfiguration.driveSize = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          break;
        case 'cacheDriveSlots':
          hardwareConfiguration.cacheDriveSlots = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'diskGroup':
          hardwareConfiguration.diskGroup = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          break;
        case 'userDefinedDiskGroups':
          hardwareConfiguration.userDefinedDiskGroups = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'serverRam':
          hardwareConfiguration.serverRam = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'networkSpeed':
          hardwareConfiguration.networkSpeed = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;

        //cluster config
        case 'clusterType':
          clusterConfiguration.clusterType = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          break;
        case 'operationsReserve':
          clusterConfiguration.operationsReserve = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          break;
        case 'hostRebuild':
          clusterConfiguration.hostRebuildReserve = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          break;
        case 'swapSpaceEnabled':
          clusterConfiguration.accountForSwapSpace = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          break;
        case 'growthFactor':
          clusterConfiguration.growthFactor = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'steadyStateHeadRoom':
          clusterConfiguration.cpuHeadroomSteadyState = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'hostFailureHeadRoom':
          clusterConfiguration.cpuHeadroomHostFailureScenario = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'storageEfficiencyPreference':
          clusterConfiguration.storageEfficiencyPreference = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          break;

        //workload details
        case 'numberOfVMs':
          workloadDetails.totalCountOfVms = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'vmdkSize':
          workloadDetails.storagePerVM = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'vcpuPerCore':
          workloadDetails.vcpuPerCore = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          clusterConfiguration.vcpuPerCore = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'vMemPerRam':
          clusterConfiguration.vMemPerRam = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'vcpuPerVM':
          workloadDetails.vcpuPerVM = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'vmemPerVM':
          workloadDetails.vmemPerVM = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'iopsPerVM':
          workloadDetails.iopsPerVM = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'compressionOnly':
        case 'compression':
          workloadDetails.compression = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'dedupRatio':
          workloadDetails.dedup = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'hostFailuresToTolerate':
          clusterConfiguration.hostFailuresToTolerate = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          workloadDetails.hostFailuresToTolerate = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          break;
        case 'raid':
          clusterConfiguration.faultToleranceMethod = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          workloadDetails.faultToleranceMethod = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          break;
        case 'ioAccessPattern':
          workloadDetails.ioAccessPattern = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          break;
        case 'ioSize':
          workloadDetails.ioSize = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          break;
        case 'ioRatio':
          workloadDetails.ioRatio = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          break;
        case 'autoMode':
          // Model gap (not fixed): DataProvider.WorkloadDetails.autoMode / ClusterConfiguration.autoMode
          // are declared as `Default` ({value, unit}), but the value actually assigned here -
          // {default: config.default} - is shaped like every other DefaultOptions2-style field in
          // this switch (minus `options`). The interface and the runtime shape disagree; preserved
          // exactly via `as unknown as`, not corrected. See "Behavior anomalies observed" in the S1
          // task report.
          workloadDetails.autoMode = {
            default: config.default,
          } as unknown as DataProvider.Default;
          clusterConfiguration.autoMode = {
            default: config.default,
          } as unknown as DataProvider.Default;
          break;
        //advancesettings
        case 'cpuCoresPerIOPS':
          advancedSettings.vSANOverheads.coresConsumed = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'capacityWriteIOAmp':
          advancedSettings.vSANOverheads.capacityWriteIOAmplifier = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'maxIopsPerDiskGroup':
          advancedSettings.vSANOverheads.maxIOpsPerDisk = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'cacheWriteIOAmp':
          advancedSettings.vSANOverheads.cacheWriteIOAmplifier = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'maxBandwidthPerDiskGroup':
          advancedSettings.vSANOverheads.maxBandwidthPerDisk = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'cpuUtilization':
          advancedSettings.resourceUtilizationPlan.cpuUtilization = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'cpuConsumption':
          advancedSettings.resourceUtilizationPlan.cpuConsumption = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'vmPercentage':
          advancedSettings.resourceUtilizationPlan.vmPercentage = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'averageMemoryUtilization':
          advancedSettings.resourceUtilizationPlan.avgMemoryConsumption = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'averageCPUUtilization':
          advancedSettings.resourceUtilizationPlan.avgConsumption = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
      }
    });
  }

  storageConfiguration = {
    storageType: {
      default: {
        value: 'VSAN',
        unit: null,
      },
      options: AppConstants.STORAGE_TYPES,
    },
    vsanArchitecture: {
      default: {
        value: 'ESA',
        unit: null,
      },
      options: AppConstants.vSAN_ARCHITECTURES,
    },
    vsanProfileMode: {
      default: {
        value: 'AF',
        unit: null,
      },
      options: AppConstants.PROFILE_MODE,
    },
  } as unknown as DataProvider.StorageConfiguration;
  storageConfiguration.vsanArchitecture.default.value = inputConfig.queries.vsanarchitecture;
  storageConfiguration.vsanProfileMode!.default.value = inputConfig.queries.profilemodecode;
  return {
    storageConfiguration: storageConfiguration,
    hardwareConfiguration: hardwareConfiguration,
    clusterConfiguration: clusterConfiguration,
    workload: workloadDefinition,
    workloadDetails: workloadDetails,
    advancedSettings: advancedSettings,
  } as DataProvider.DefaultInputConfig;
}

export interface HftRaidsConfig {
  hostFailuresToTolerate?: DataProvider.DefaultOptions2;
  faultToleranceMethod?: DataProvider.DefaultOptions2;
}

/**
 * Ported as-is from AdapterService.getHftRaidsFromDataProviderAPIResponse (adapter.service.ts:599-620).
 */
export function getHftRaidsFromDataProviderAPIResponse(inputConfig: DataProvider.Configuration[]): HftRaidsConfig {
  const hftRaids: HftRaidsConfig = {};
  if (inputConfig && inputConfig.length > 0) {
    inputConfig.forEach((config: DataProvider.Configuration) => {
      switch (config.id) {
        case 'hostFailuresToTolerate':
          hftRaids.hostFailuresToTolerate = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          break;
        case 'raid':
          hftRaids.faultToleranceMethod = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          break;
      }
    });
  }
  return hftRaids;
}

export interface IoProfileConfig {
  ioAccessPattern?: DataProvider.DefaultOptions2;
  ioSize?: DataProvider.DefaultOptions2;
  ioRatio?: DataProvider.DefaultOptions2;
}

/**
 * Ported as-is from AdapterService.getIoProfileFromDataProviderAPIResponse (adapter.service.ts:622-652).
 */
export function getIoProfileFromDataProviderAPIResponse(inputConfig: DataProvider.Configuration[]): IoProfileConfig {
  const ioProfileDetails: IoProfileConfig = {};
  if (inputConfig && inputConfig.length > 0) {
    inputConfig.forEach((config: DataProvider.Configuration) => {
      switch (config.id) {
        case 'ioAccessPattern':
          ioProfileDetails.ioAccessPattern = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          break;
        case 'ioSize':
          ioProfileDetails.ioSize = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          break;
        case 'ioRatio':
          ioProfileDetails.ioRatio = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          break;
      }
    });
  }
  if (ioProfileDetails.ioAccessPattern?.default?.value?.toLocaleLowerCase() === 'SEQUENTIAL'.toLocaleLowerCase()) {
    ioProfileDetails.ioRatio = AppConstants.IO_ACCESS_PATTEN_SEQUENTIAL_IO_RATIO as unknown as DataProvider.DefaultOptions2;
  }
  return ioProfileDetails;
}

export type VsanOverheadsConfig = Partial<DataProvider.VSANOverheads>;

/**
 * Ported as-is from AdapterService.getvSANOverheadsFromDataProviderAPIResponse (adapter.service.ts:654-703).
 *
 * Behavior anomaly preserved as-is: the `return vSANOverheads` statement is inside the
 * `if (inputConfig && inputConfig.length > 0)` block, so this returns `undefined` (not `{}`) when
 * called with an empty or falsy array - inconsistent with the sibling
 * getHftRaidsFromDataProviderAPIResponse, which always returns an object. Not fixed here.
 */
export function getvSANOverheadsFromDataProviderAPIResponse(
  inputConfig: DataProvider.Configuration[],
): VsanOverheadsConfig | undefined {
  const vSANOverheads: VsanOverheadsConfig = {};
  if (inputConfig && inputConfig.length > 0) {
    inputConfig.forEach((config: DataProvider.Configuration) => {
      switch (config.id) {
        case 'cpuCoresPerIOPS':
          vSANOverheads.coresConsumed = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'capacityWriteIOAmp':
          vSANOverheads.capacityWriteIOAmplifier = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'maxIopsPerDiskGroup':
          vSANOverheads.maxIOpsPerDisk = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'cacheWriteIOAmp':
          vSANOverheads.cacheWriteIOAmplifier = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'maxBandwidthPerDiskGroup':
          vSANOverheads.maxBandwidthPerDisk = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
      }
    });
    return vSANOverheads;
  }
  return undefined;
}

export interface WorkloadTypeConfig {
  workloadDetails: Partial<DataProvider.WorkloadDetails>;
  advancedSettings: {
    vSANOverheads: Partial<DataProvider.VSANOverheads>;
    resourceUtilizationPlan: ResourceUtilizationPlanExtended;
  };
}

/**
 * Ported as-is from AdapterService.getWorkloadTypeConfigFromDataProviderAPIResponse (adapter.service.ts:705-963).
 *
 * Note the resourceUtilizationPlan hardcoded max/min/value literals (100/1/100, 100/1/99) for
 * cpuUtilization/cpuConsumption/vmPercentage below - unlike every other field in this file, which
 * reads max/min/value from `config.limits`/`config.default`. Preserved exactly as in the source;
 * not unified with getInputConfigFromDataProviderAPIResponse's equivalent (which does read from
 * config). See "Behavior anomalies observed" in the S1 task report.
 */
export function getWorkloadTypeConfigFromDataProviderAPIResponse(
  workloadTypeConfigurationRequest: DataProvider.Configuration[],
): WorkloadTypeConfig {
  const workloadDetails: Partial<DataProvider.WorkloadDetails> = {};
  const advancedSettings: {
    vSANOverheads: Partial<DataProvider.VSANOverheads>;
    resourceUtilizationPlan: ResourceUtilizationPlanExtended;
  } = {
    vSANOverheads: {},
    resourceUtilizationPlan: {},
  };
  if (workloadTypeConfigurationRequest && workloadTypeConfigurationRequest.length > 0) {
    workloadTypeConfigurationRequest.forEach((config: DataProvider.Configuration) => {
      switch (config.id) {
        case 'numberOfVMs':
          workloadDetails.totalCountOfVms = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'masterVmSize':
          workloadDetails.sizeOfMasterVM = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'disksInMasterVm':
          workloadDetails.numOfDisksInMasterVM = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'linkedClonePools':
          workloadDetails.numOfLinkedClonePools = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'expectedVmdkGrowth':
          workloadDetails.expecVmdkGrowth = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'persistentDiskSize':
          workloadDetails.persistentDiskSize = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'systemDisposableDisk':
          workloadDetails.sysDisposableDiskSize = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'instantClonePools':
          workloadDetails.numOfInstClonePools = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'expectedCloneGrowth':
          workloadDetails.expecCloneGrowth = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'vmdkSize':
          workloadDetails.storagePerVM = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'vcpuPerCore':
          workloadDetails.vcpuPerCore = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'vcpuPerVM':
          workloadDetails.vcpuPerVM = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'vmemPerVM':
          workloadDetails.vmemPerVM = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'iopsPerVM':
          workloadDetails.iopsPerVM = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'compressionOnly':
        case 'compression':
          workloadDetails.compression = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'dedupRatio':
          workloadDetails.dedup = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'hostFailuresToTolerate':
          workloadDetails.hostFailuresToTolerate = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          break;
        case 'raid':
          workloadDetails.faultToleranceMethod = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          break;
        case 'ioAccessPattern':
          workloadDetails.ioAccessPattern = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          break;
        case 'ioSize':
          workloadDetails.ioSize = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          break;
        case 'ioRatio':
          workloadDetails.ioRatio = {
            default: config.default,
            options: config.options,
          } as DataProvider.DefaultOptions2;
          break;
        case 'autoMode':
          // Model gap (not fixed) - see the comment on the ResourceUtilizationPlanExtended type above.
          workloadDetails.autoMode = {
            default: config.default,
          } as unknown as DataProvider.Default;
          break;
        //advancesettings
        case 'cpuCoresPerIOPS':
          advancedSettings.vSANOverheads.coresConsumed = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'capacityWriteIOAmp':
          advancedSettings.vSANOverheads.capacityWriteIOAmplifier = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'maxIopsPerDiskGroup':
          advancedSettings.vSANOverheads.maxIOpsPerDisk = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'cacheWriteIOAmp':
          advancedSettings.vSANOverheads.cacheWriteIOAmplifier = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'maxBandwidthPerDiskGroup':
          advancedSettings.vSANOverheads.maxBandwidthPerDisk = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'cpuUtilization':
          advancedSettings.resourceUtilizationPlan.cpuUtilization = {
            max: 100,
            min: 1,
            value: 100,
            unit: config.default.unit,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any;
          break;
        case 'cpuConsumption':
          advancedSettings.resourceUtilizationPlan.cpuConsumption = {
            max: 100,
            min: 1,
            value: 100,
            unit: config.default.unit,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any;
          break;
        case 'vmPercentage':
          advancedSettings.resourceUtilizationPlan.vmPercentage = {
            max: 100,
            min: 1,
            value: 99,
            unit: config.default.unit,
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
          } as any;
          break;
        case 'averageMemoryUtilization':
          advancedSettings.resourceUtilizationPlan.avgMemoryConsumption = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
        case 'averageCPUUtilization':
          advancedSettings.resourceUtilizationPlan.avgConsumption = {
            max: config.limits?.max.value,
            min: config.limits?.min.value,
            value: config.default.value,
            unit: config.default.unit,
          } as DataProvider.ValueUnit;
          break;
      }
    });
  }
  return { workloadDetails, advancedSettings };
}

const toLimitedValueUnit = (config: DataProvider.Configuration): DataProvider.ValueUnit => ({
  max: config.limits?.max?.value as unknown as number | undefined,
  min: config.limits?.min?.value as unknown as number | undefined,
  value: config.default?.value,
  unit: config.default?.unit ?? null,
}) as DataProvider.ValueUnit;

/**
 * Maps the clusterType=D_HCI Configuration API response onto the controls whose limits/defaults
 * differ from HCI's: network speed (single / E-W when traffic separation is on / N-S), and the
 * separate compute / storage growth factors, plus HFT / RAID defaults and options. Other ids
 * are ignored. Missing ids are left out of the overlay (hardware controls keep their HCI-derived
 * limits; resilience controls wait for a deployment-specific response) - never throws, so a partial response can't break the form.
 * N/S accepts explicit limits or an option list; options supply bounds absent from limits.
 */
export function getDhciInputOverlayFromDataProviderAPIResponse(
  response: DataProvider.InputConfigurationResponse | undefined,
): DataProvider.DhciInputOverlay {
  const overlay: DataProvider.DhciInputOverlay = {};
  for (const config of response?.configurations ?? []) {
    switch (config.id) {
      case 'hostFailuresToTolerate':
        overlay.hostFailuresToTolerate = { default: config.default, options: config.options } as DataProvider.DefaultOptions2;
        break;
      case 'raid':
        overlay.faultToleranceMethod = { default: config.default, options: config.options } as DataProvider.DefaultOptions2;
        break;
      case 'networkSpeed':
        overlay.networkSpeed = toLimitedValueUnit(config);
        break;
      case 'networkSpeedEW':
        overlay.networkSpeedEW = toLimitedValueUnit(config);
        break;
      case 'networkSpeedNS': {
        const options = (config.options ?? [])
          .filter(o => o.value != null && String(o.value).trim() !== '')
          .map(o => Number(o.value)).filter(Number.isFinite);
        overlay.networkSpeedNS = {
          min: config.limits?.min?.value ?? (options.length ? Math.min(...options) : undefined),
          max: config.limits?.max?.value ?? (options.length ? Math.max(...options) : undefined),
          value: config.default?.value,
          unit: config.default?.unit ?? null,
        } as DataProvider.ValueUnit;
        break;
      }
      case 'computeGrowthFactor':
        overlay.computeGrowthFactor = toLimitedValueUnit(config);
        break;
      case 'storageGrowthFactor':
        overlay.storageGrowthFactor = toLimitedValueUnit(config);
        break;
    }
  }
  return overlay;
}
